/* Sprint 2R-2B — Admin Pilot Status panel.
 *
 * Page-load snapshot + manual "Re-run checks" button. No persistent polling
 * (user explicitly opted out of constant polling for pilot).
 */
import React from "react";
import { adminApi } from "../../lib/api";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Loader2, Activity } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  green: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", label: "OK" },
  yellow: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", label: "Advarsel" },
  red: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", label: "Blokerer" },
};

const OVERALL_STYLE = {
  ready: { color: "bg-emerald-600", label: "Klar til pilot" },
  ready_with_warnings: { color: "bg-amber-500", label: "Klar med advarsler" },
  blocked: { color: "bg-red-600", label: "Blokerer pilot — fix nødvendigt" },
};

export default function PilotStatusPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  const load = React.useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const r = await adminApi().get("/admin/pilot-status");
      setData(r.data);
      if (manual) toast.success("Pilot Status genkørt");
    } catch (e) {
      toast.error("Kunne ikke hente Pilot Status");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(false); }, [load]);

  if (loading) return (
    <div className="flex items-center gap-2 text-brand-text2"><Loader2 className="animate-spin" size={16}/> Kører health-checks...</div>
  );

  const overall = OVERALL_STYLE[data?.overall] || { color: "bg-slate-500", label: data?.overall };

  return (
    <div className="space-y-6" data-testid="admin-pilot-status-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Activity size={20} className="text-brand-red"/>
            <h2 className="font-display font-bold text-xl text-brand-dark">Pilot Status</h2>
          </div>
          <p className="text-sm text-brand-text2 mt-1">
            System-tjek før pilot-lancering. Page-load snapshot + manuel re-run.
          </p>
          {data?.fetched_at_iso && (
            <p className="text-xs text-brand-text2 mt-1" data-testid="pilot-fetched-at">
              Sidst kørt: {new Date(data.fetched_at_iso).toLocaleString("da-DK")}
            </p>
          )}
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="px-4 py-2 bg-brand-red hover:bg-red-700 text-white text-sm inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="pilot-status-rerun-btn"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""}/> Re-run checks
        </button>
      </div>

      {/* Overall status */}
      <div className={`p-4 ${overall.color} text-white flex items-center justify-between flex-wrap gap-3`} data-testid="pilot-overall-status">
        <div className="font-display font-bold text-lg">{overall.label}</div>
        <div className="text-sm flex items-center gap-4">
          <span data-testid="pilot-summary-green">🟢 {data?.summary?.green || 0} OK</span>
          <span data-testid="pilot-summary-yellow">🟡 {data?.summary?.yellow || 0} Advarsler</span>
          <span data-testid="pilot-summary-red">🔴 {data?.summary?.red || 0} Blokerer</span>
        </div>
      </div>

      {/* Checks grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pilot-checks-grid">
        {(data?.checks || []).map((check) => {
          const style = STATUS_STYLE[check.status] || STATUS_STYLE.yellow;
          const Icon = style.icon;
          return (
            <div
              key={check.key}
              className={`p-4 border ${style.border} ${style.bg}`}
              data-testid={`pilot-check-${check.key}`}
            >
              <div className="flex items-start gap-3">
                <Icon className={`${style.color} mt-0.5 flex-shrink-0`} size={18}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="font-display font-semibold text-brand-dark text-sm">{check.label}</div>
                    <span className={`text-xs uppercase font-bold ${style.color}`}>{style.label}</span>
                  </div>
                  <div className="text-xs text-brand-text2 leading-relaxed">{check.detail}</div>
                  {/* Extra metrics */}
                  {(check.online_drivers != null || check.active_routes != null || check.vehicles != null) && (
                    <div className="mt-2 flex gap-3 text-xs text-brand-text2">
                      {check.online_drivers != null && <span>Online: <b>{check.online_drivers}</b></span>}
                      {check.active_routes != null && <span>Aktive ruter: <b>{check.active_routes}</b></span>}
                      {check.vehicles != null && <span>Køretøjer: <b>{check.vehicles}</b></span>}
                      {check.route_records != null && <span>ESG records: <b>{check.route_records}</b></span>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-xs text-brand-text2">
        Tip: <b>Gul</b> betyder ikke nødvendigvis fejl — fx mock-tilstand for SMS/SMTP er forventet før pilot. <b>Rød</b> blokerer derimod for pilot-launch.
      </div>
    </div>
  );
}
